/**
 * Koreksi Absen — Q37 (KEPUTUSAN_MODUL_TAMBAHAN_ETAMSIOS_05082026.md §Q37).
 * Karyawan ber-akun mengajukan untuk DIRINYA SENDIRI (tidak ada pilihan karyawan — KC tidak membuatkan);
 * staf produksi mengajukan lewat kios (menu "Ajukan Koreksi"). Kepala Cabang region memutus, Owner semua region;
 * pemohon tidak memutus. Yang disetujui tercatat di absensi sebagai KOREKSI, bukan absen biasa.
 * Tombol putus mengikuti jawaban backend; layar tidak menebak hak.
 * BUSINESS-55: paling lambat 3 hari kerja sesudah kejadian dan sebelum periode gaji difinalkan; lewat batas ditolak,
 * kecuali diajukan sebagai pengecualian (ditanyakan dulu) — pengecualian hanya diputus Owner.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { konfirmasi, mintaTeks } from "../lib/dialogAplikasi";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { formatDate, formatDateTime } from "../lib/format";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, Paginasi } from "../components/etams/PalangDaftar";

const KATA_STATUS = { DIAJUKAN: "Menunggu keputusan", DISETUJUI: "Disetujui", DITOLAK: "Ditolak", DIBATALKAN: "Dibatalkan" };
const KOSONG = { tanggal: "", jam_masuk: "", jam_pulang: "", alasan: "" };
//: Cermin `URUT_SAH` di backend/services/koreksi_absensi.py.
const URUT_SAH = ["created_at", "tanggal", "status"];
const KOLOM_CSV = [
  { kunci: "karyawan", judul: "Karyawan", ambil: (x) => x.employee?.full_name || "" },
  { kunci: "tanggal", judul: "Tanggal" },
  { kunci: "status", judul: "Status", ambil: (x) => KATA_STATUS[x.status] || x.status },
  { kunci: "jalur", judul: "Jalur", ambil: (x) => (x.jalur === "KIOS" ? "kios" : "akun") },
  { kunci: "alasan", judul: "Alasan" },
];

export default function HRKoreksiAbsen() {
  const { profile, roleCode } = useAuth();
  const [baris, setBaris] = useState(null);
  const [total, setTotal] = useState(0);
  const [rekap, setRekap] = useState([]);
  const [saya, setSaya] = useState(undefined);        // undefined = memuat, null = akun belum ditautkan
  const [form, setForm] = useState(KOSONG);
  const [sibuk, setSibuk] = useState(false);
  const pemutus = roleCode === "OWNER" || roleCode === "KEPALA_CABANG";

  // Pola daftar seragam (28 Sep 2026): koreksi absen tumbuh tiap hari — halaman, cari, dan urut di SERVER.
  // Saringan bawaan untuk pemutus: "Menunggu keputusan" (chip bisa dihapus untuk melihat semuanya).
  const daftar = useDaftar(baris || [], {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "created_at", arah: "desc" },
    saringan: {
      status: { label: "Status", param: "status", bawaan: pemutus ? "DIAJUKAN" : "", tampil: (v) => KATA_STATUS[v] || v },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const bangunParams = useCallback((o = {}) => ({ ...paramSaringan, ...(cari && { q: cari }),
    limit, offset, sort, arah, ...o }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await api.get("/hr/koreksi-absen", { params: bangunParams({ offset: off, limit: n }) })).data, [bangunParams]);

  const muatDaftar = useCallback(async () => {
    try {
      const r = await api.get("/hr/koreksi-absen", { params: bangunParams() });
      setBaris(r.data.items || []);
      setTotal(typeof r.data.total === "number" ? r.data.total : (r.data.items || []).length);
    } catch (e) { toast.error(apiErrorMessage(e, "Daftar koreksi gagal dimuat.")); setBaris([]); setTotal(0); }
  }, [bangunParams]);
  useEffect(() => { muatDaftar(); }, [muatDaftar]);

  const muat = useCallback(async () => {
    await muatDaftar();
    if (pemutus) {
      try { const r = await api.get("/hr/koreksi-absen/rekap"); setRekap(r.data.items || []); }
      catch (e) { toast.error(apiErrorMessage(e, "Rekap koreksi gagal dimuat.")); }
    }
  }, [pemutus, muatDaftar]);
  useEffect(() => {
    if (!pemutus) return;
    api.get("/hr/koreksi-absen/rekap").then((r) => setRekap(r.data.items || []))
      .catch((e) => toast.error(apiErrorMessage(e, "Rekap koreksi gagal dimuat.")));
  }, [pemutus]);

  useEffect(() => {
    (async () => {
      // Pintu yang sama dengan layar Izin (sensus pintu): 404 EMPLOYEE_NOT_LINKED = akun belum ditautkan.
      try { const r = await api.get("/hr/attendance/saya/hari-ini"); setSaya(r.data.employee || null); }
      catch { setSaya(null); }
    })();
  }, []);

  const kirim = async (pengecualian = false) => {
    setSibuk(true);
    let tawarPengecualian = null;
    try {
      const r = await api.post("/hr/koreksi-absen", {
        tanggal: form.tanggal, alasan: form.alasan.trim(),
        jam_masuk: form.jam_masuk || null, jam_pulang: form.jam_pulang || null, pengecualian,
      });
      toast.success(r.data.lewat_batas ? "Pengecualian diajukan — menunggu keputusan Owner."
                                       : "Koreksi diajukan — menunggu keputusan Kepala Cabang.");
      setForm(KOSONG);
      await muat();
    } catch (e) {
      if (!pengecualian && ["KOREKSI_LEWAT_BATAS", "PERIODE_GAJI_DITUTUP"].includes(e?.response?.data?.detail?.error)) {
        tawarPengecualian = e.response.data.detail.message;
      } else {
        toast.error(apiErrorMessage(e, "Pengajuan koreksi gagal."));
      }
    } finally { setSibuk(false); }
    if (tawarPengecualian && await konfirmasi(
      `${tawarPengecualian}\n\nAjukan sebagai pengecualian? Hanya Owner yang memutus, dan tercatat.`,
      { judul: "Lewat batas koreksi", ya: "Ajukan pengecualian" })) {
      await kirim(true);
    }
  };

  const putus = async (x, setuju) => {
    let alasan = null;
    if (!setuju) {
      alasan = await mintaTeks("Alasan menolak koreksi ini:", { minPanjang: 5 });
      if (!alasan) return;
    }
    setSibuk(true);
    try {
      const r = await api.post(`/hr/koreksi-absen/${x.id}/putuskan`, { setuju, alasan });
      toast.success(setuju ? (r.data?.penyesuaian_gaji || "Disetujui — absen tanggal itu tercatat sebagai koreksi.") : "Koreksi ditolak.");
      await muat();
    } catch (e) { toast.error(apiErrorMessage(e, "Keputusan gagal disimpan.")); }
    finally { setSibuk(false); }
  };

  const batal = async (x) => {
    setSibuk(true);
    try { await api.post(`/hr/koreksi-absen/${x.id}/batalkan`); toast.success("Koreksi dibatalkan."); await muat(); }
    catch (e) { toast.error(apiErrorMessage(e, "Pembatalan gagal.")); }
    finally { setSibuk(false); }
  };

  const jam = (x, k) => (x[k] ? formatDateTime(x[k], x.region?.timezone).split(" ").slice(3).join(" ") : "—");

  return (
    <div className="space-y-4" data-testid="koreksi-absen-page">
      <div>
        <h1 className="text-xl font-semibold">Koreksi Absen</h1>
        <p className="text-sm text-muted-foreground">
          Lupa absen atau alat bermasalah? Ajukan koreksi untuk diri sendiri: tanggal, jam yang seharusnya, dan alasannya —
          paling lambat 3 hari kerja sesudah kejadian dan sebelum periode gaji difinalkan (lewat itu hanya sebagai
          pengecualian yang diputus Owner).
          Staf tanpa akun mengajukan lewat kios (menu Ajukan Koreksi). Kepala Cabang region memutus; koreksi Kepala Cabang
          diputus Owner. Yang disetujui tercatat di absensi sebagai hasil koreksi, bukan absen biasa.
        </p>
      </div>

      {saya === null ? (
        <Card className="p-4 text-sm text-muted-foreground" data-testid="koreksi-belum-tertaut">
          Akun ini belum ditautkan ke data karyawan, jadi tidak bisa mengajukan koreksi dari sini.
        </Card>
      ) : saya && (
        <Card className="p-4 space-y-3" data-testid="koreksi-form">
          <div className="text-sm">Karyawan: <span className="font-medium">{saya.full_name}</span>{saya.employee_code ? ` (${saya.employee_code})` : ""}</div>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="text-sm space-y-1">
              <span>Tanggal</span>
              <Input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} data-testid="koreksi-tanggal" />
            </label>
            <label className="text-sm space-y-1">
              <span>Jam masuk seharusnya</span>
              <Input type="time" value={form.jam_masuk} onChange={(e) => setForm({ ...form, jam_masuk: e.target.value })} data-testid="koreksi-jam-masuk" />
            </label>
            <label className="text-sm space-y-1">
              <span>Jam pulang seharusnya</span>
              <Input type="time" value={form.jam_pulang} onChange={(e) => setForm({ ...form, jam_pulang: e.target.value })} data-testid="koreksi-jam-pulang" />
            </label>
          </div>
          <p className="text-xs text-muted-foreground">Isi salah satu atau keduanya — jam yang dikosongkan tidak diubah.</p>
          <Textarea placeholder="Alasan (minimal 5 karakter)" value={form.alasan}
                    onChange={(e) => setForm({ ...form, alasan: e.target.value })} data-testid="koreksi-alasan" />
          <Button disabled={sibuk || !form.tanggal || (!form.jam_masuk && !form.jam_pulang) || form.alasan.trim().length < 5}
                  onClick={() => kirim(false)} data-testid="koreksi-kirim">Ajukan koreksi</Button>
        </Card>
      )}

      {pemutus && rekap.length > 0 && (
        <Card className="p-4 space-y-2" data-testid="koreksi-rekap">
          <div className="font-medium text-sm">Seberapa sering tiap karyawan mengajukan koreksi</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr><th className="py-1 pr-3">Karyawan</th><th className="py-1 pr-3">Jumlah</th><th className="py-1 pr-3">Disetujui</th>
                  <th className="py-1 pr-3">Ditolak</th><th className="py-1 pr-3">Menunggu</th><th className="py-1 pr-3">Lewat kios</th><th className="py-1">Terakhir</th></tr>
              </thead>
              <tbody>
                {rekap.map((r) => (
                  <tr key={r.employee_id} className="border-t" data-testid={`koreksi-rekap-${r.employee_id}`}>
                    <td className="py-1 pr-3">{r.full_name || "—"}{r.employee_code ? ` (${r.employee_code})` : ""}</td>
                    <td className="py-1 pr-3 font-medium">{r.jumlah}</td>
                    <td className="py-1 pr-3">{r.DISETUJUI}</td>
                    <td className="py-1 pr-3">{r.DITOLAK}</td>
                    <td className="py-1 pr-3">{r.DIAJUKAN}</td>
                    <td className="py-1 pr-3">{r.lewat_kios}</td>
                    <td className="py-1">{formatDate(r.terakhir)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <PalangDaftar daftar={daftar} placeholder="Cari nama karyawan / alasan…" testId="koreksi-palang"
                    ekspor={{ nama: "koreksi_absen", kolom: KOLOM_CSV, ambilHalaman }}>
        <select className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                value={daftar.nilaiSaringan.status} onChange={(e) => daftar.setSaringan("status", e.target.value)}
                aria-label="Saring status" data-testid="koreksi-filter-status">
          <option value="">Semua status</option>
          {Object.entries(KATA_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </PalangDaftar>
      {baris === null ? <div className="text-sm text-muted-foreground">Memuat…</div> : daftar.total === 0 ? (
        <Card className="p-6 text-sm text-muted-foreground" data-testid="koreksi-kosong">
          {daftar.cari.trim() || daftar.saringanAktif.length > 0 ? "Tidak ada yang cocok." : "Belum ada koreksi absen."}
        </Card>
      ) : (
        <div className="space-y-2">
          {daftar.tampil.map((x) => (
            <Card key={x.id} className="p-4 flex flex-wrap items-start justify-between gap-3" data-testid={`koreksi-${x.id}`}>
              <div className="text-sm space-y-1">
                <div className="font-medium">{x.employee?.full_name || "—"} · {formatDate(x.tanggal)}</div>
                <div>Masuk {jam(x, "jam_masuk")} · Pulang {jam(x, "jam_pulang")}</div>
                <div className="text-muted-foreground">
                  {KATA_STATUS[x.status] || x.status} · {x.jalur === "KIOS" ? "diajukan lewat kios" : "diajukan dari akun"}
                  {x.lewat_batas ? " · pengecualian lewat batas (diputus Owner)" : ""}
                </div>
                <div>{x.alasan}</div>
                {x.alasan_putus && <div className="text-xs text-muted-foreground">Catatan keputusan: {x.alasan_putus}</div>}
              </div>
              {x.status === "DIAJUKAN" && (
                <div className="flex gap-2">
                  {x.diajukan_oleh && x.diajukan_oleh === profile?.id ? (
                    <Button size="sm" variant="outline" disabled={sibuk} onClick={() => batal(x)} data-testid={`koreksi-batal-${x.id}`}>Batalkan</Button>
                  ) : pemutus && (!x.lewat_batas || roleCode === "OWNER") && (
                    <>
                      <Button size="sm" disabled={sibuk} onClick={() => putus(x, true)} data-testid={`koreksi-setuju-${x.id}`}>Setujui</Button>
                      <Button size="sm" variant="outline" disabled={sibuk} onClick={() => putus(x, false)} data-testid={`koreksi-tolak-${x.id}`}>Tolak</Button>
                    </>
                  )}
                </div>
              )}
            </Card>
          ))}
          <Paginasi daftar={daftar} testId="koreksi-paginasi" />
        </div>
      )}
    </div>
  );
}
